import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowCreateBrandProviderModal } from '@hooks/useBrandProvider'
import type { ProviderModel } from '@models/types'

import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateOrEditApiKeyModal from '@modals/CreateOrEditApiKeyModal'
import CreateOrEditOrderLeadTimeModal from '@modals/CreateOrEditOrderLeadTimeModal'
import CreateOrEditProviderDeadlineGroupModal from '@modals/CreateOrEditProviderDeadlineGroupModal'
import CreateOrEditProviderDisclaimerModal from '@modals/CreateOrEditProviderDisclaimerModal'
import CreateOrEditProviderOrderCodeModal from '@modals/CreateOrEditProviderOrderCodeModal'
import CreateOrEditProviderProductCodeModal from '@modals/CreateOrEditProviderProductCodeModal'
import CreateOrEditProviderServiceModal from '@modals/CreateOrEditProviderServiceModal'
import CreateOrEditWebhookModal from '@modals/CreateOrEditWebhookModal'
import CreateProviderPackageModal from '@modals/CreateProviderPackageModal'
import CreateUserModal from '@modals/CreateUserModal/CreateUserModal'
import EditProviderTradingDaysModal from '@modals/EditProviderTradingDaysModal'
import GenerateReportModal from '@modals/GenerateReportModal'
import ManageProviderApiSettingsModal from '@modals/ManageProviderApiSettingsModal'
import CreateOrEditProviderDeepLinkModal from '@modals/CreateOrEditProviderDeepLinkModal'
import ManageProviderOptionsModal from '@modals/ManageProviderOptionsModal'
import ManageProviderOrderDeliveryModal from '@modals/ManageProviderOrderDeliveryModal'

import PageContent from './components/PageContent'

const defaultState = {
  showBulkCreateBrandProviderModal: false,
  showCreateOrEditApiKeyModal: false,
  showCreateOrEditOrderLeadTimeModal: false,
  showCreateOrEditProviderDeadlineGroupModal: false,
  showCreateOrEditProviderDisclaimerModal: false,
  showCreateOrEditProviderOrderCodeModal: false,
  showCreateOrEditProviderProductCodeModal: false,
  showCreateOrEditProviderServiceModal: false,
  showCreateOrEditWebhookModal: false,
  showCreateProviderPackageModal: false,
  showCreateUserModal: false,
  showEditProviderTradingDaysModal: false,
  showGenerateReportModal: false,
  showManageProviderApiSettingsModal: false,
  showCreateOrEditProviderDeepLinkModal: false,
  showManageProviderOptionsModal: false,
  showManageProviderOrderDeliveryModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditApiKeyModal: {
      closeModal: () => setState({ showCreateOrEditApiKeyModal: false }),
      createApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditOrderLeadTimeModal: {
      closeModal: () => setState({ showCreateOrEditOrderLeadTimeModal: false }),
      createOrderLeadTime: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderLeadTime: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderLeadTime: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderDeadlineGroupModal: {
      closeModal: () => setState({ showCreateOrEditProviderDeadlineGroupModal: false }),
      createProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setAsDefault: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderProductCodeModal: {
      closeModal: () => setState({ showCreateOrEditProviderProductCodeModal: false }),
      createProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderDisclaimerModal: {
      closeModal: () => setState({ showCreateOrEditProviderDisclaimerModal: false }),
      createProviderDisclaimer: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderDisclaimer: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderDisclaimer: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderOrderCodeModal: {
      closeModal: () => setState({ showCreateOrEditProviderOrderCodeModal: false }),
      createProviderOrderCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderOrderCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderServiceModal: {
      closeModal: () => setState({ showCreateOrEditProviderServiceModal: false }),
      createProviderService: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderService: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderService: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditWebhookModal: {
      closeModal: () => setState({ showCreateOrEditWebhookModal: false }),
      createWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateProviderPackageModal: {
      closeModal: () => setState({ showCreateProviderPackageModal: false }),
      createProviderPackage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateUserModal: {
      closeModal: () => setState({ showCreateUserModal: false }),
      createUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditProviderTradingDaysModal: {
      closeModal: () => setState({ showEditProviderTradingDaysModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    GenerateReportModal: {
      closeModal: () => setState({ showGenerateReportModal: false }),
      generateReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderApiSettingsModal: {
      closeModal: () => setState({ showManageProviderApiSettingsModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderDeepLinkModal: {
      closeModal: () => setState({ showCreateOrEditProviderDeepLinkModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderOptionsModal: {
      closeModal: () => setState({ showManageProviderOptionsModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderOrderDeliveryModal: {
      closeModal: () => setState({ showManageProviderOrderDeliveryModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateBrandProviderModal,
    showCreateOrEditApiKeyModal,
    showCreateOrEditOrderLeadTimeModal,
    showCreateOrEditProviderDeadlineGroupModal,
    showCreateOrEditProviderDisclaimerModal,
    showCreateOrEditProviderOrderCodeModal,
    showCreateOrEditProviderProductCodeModal,
    showCreateOrEditProviderServiceModal,
    showCreateOrEditWebhookModal,
    showCreateProviderPackageModal,
    showCreateUserModal,
    showEditProviderTradingDaysModal,
    showGenerateReportModal,
    showManageProviderApiSettingsModal,
    showCreateOrEditProviderDeepLinkModal,
    showManageProviderOptionsModal,
    showManageProviderOrderDeliveryModal,
  } = state

  const { providerId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderLeadTime: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderDisclaimer: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderService: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderOrderCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      exportProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateBrandProviderModal: (payload: ShowCreateBrandProviderModal) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditApiKeyModal: (payload) => {
        setModalData('CreateOrEditApiKeyModal', payload)
        setState({ showCreateOrEditApiKeyModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditOrderLeadTimeModal: (payload) => {
        setModalData('CreateOrEditOrderLeadTimeModal', payload)
        setState({ showCreateOrEditOrderLeadTimeModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditProviderDeadlineGroupModal: (payload) => {
        setModalData('CreateOrEditProviderDeadlineGroupModal', payload)
        setState({ showCreateOrEditProviderDeadlineGroupModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditProviderProductCodeModal: (payload) => {
        setModalData('CreateOrEditProviderProductCodeModal', payload)
        setState({ showCreateOrEditProviderProductCodeModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditProviderDisclaimerModal: (payload) => {
        setModalData('CreateOrEditProviderDisclaimerModal', payload)
        setState({ showCreateOrEditProviderDisclaimerModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditProviderOrderCodeModal: (payload) => {
        setModalData('CreateOrEditProviderOrderCodeModal', payload)
        setState({ showCreateOrEditProviderOrderCodeModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditProviderServiceModal: (payload) => {
        setModalData('CreateOrEditProviderServiceModal', payload)
        setState({ showCreateOrEditProviderServiceModal: true })
      },
      // @ts-expect-error TS7006
      showCreateOrEditWebhookModal: (payload) => {
        setModalData('CreateOrEditWebhookModal', payload)
        setState({ showCreateOrEditWebhookModal: true })
      },
      // @ts-expect-error TS7006
      showCreateProviderPackageModal: (payload) => {
        setModalData('CreateProviderPackageModal', payload)
        setState({ showCreateProviderPackageModal: true })
      },
      // @ts-expect-error TS7006
      showCreateUserModal: (payload) => {
        setModalData('CreateUserModal', payload)
        setState({ showCreateUserModal: true })
      },
      // @ts-expect-error TS7006
      showGenerateReportModal: (payload) => {
        setModalData('GenerateReportModal', payload)
        setState({ showGenerateReportModal: true })
      },
      // @ts-expect-error TS7006
      showEditProviderTradingDaysModal: (payload) => {
        setModalData('EditProviderTradingDaysModal', payload)
        setState({ showEditProviderTradingDaysModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderApiSettingsModal: (payload) => {
        setModalData('ManageProviderApiSettingsModal', payload)
        setState({ showManageProviderApiSettingsModal: true })
      },
      showCreateOrEditProviderDeepLinkModal: (payload: { provider: ProviderModel }) => {
        setModalData('CreateOrEditProviderDeepLinkModal', payload)
        setState({ showCreateOrEditProviderDeepLinkModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderOptionsModal: (payload) => {
        setModalData('ManageProviderOptionsModal', payload)
        setState({ showManageProviderOptionsModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderOrderDeliveryModal: (payload) => {
        setModalData('ManageProviderOrderDeliveryModal', payload)
        setState({ showManageProviderOrderDeliveryModal: true })
      },
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent providerId={providerId} />

        <BulkCreateBrandProviderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateOrEditApiKeyModal
          callbacks={callbacks('CreateOrEditApiKeyModal', setState)}
          showModal={showCreateOrEditApiKeyModal}
        />

        <CreateOrEditOrderLeadTimeModal
          callbacks={callbacks('CreateOrEditOrderLeadTimeModal', setState)}
          showModal={showCreateOrEditOrderLeadTimeModal}
        />

        <CreateOrEditProviderDeadlineGroupModal
          callbacks={callbacks('CreateOrEditProviderDeadlineGroupModal', setState)}
          showModal={showCreateOrEditProviderDeadlineGroupModal}
        />

        <CreateOrEditProviderProductCodeModal
          callbacks={callbacks('CreateOrEditProviderProductCodeModal', setState)}
          showModal={showCreateOrEditProviderProductCodeModal}
        />

        <CreateOrEditProviderDisclaimerModal
          callbacks={callbacks('CreateOrEditProviderDisclaimerModal', setState)}
          showModal={showCreateOrEditProviderDisclaimerModal}
        />

        <CreateOrEditProviderOrderCodeModal
          callbacks={callbacks('CreateOrEditProviderOrderCodeModal', setState)}
          showModal={showCreateOrEditProviderOrderCodeModal}
        />

        <CreateOrEditProviderServiceModal
          callbacks={callbacks('CreateOrEditProviderServiceModal', setState)}
          showModal={showCreateOrEditProviderServiceModal}
        />

        <CreateOrEditWebhookModal
          callbacks={callbacks('CreateOrEditWebhookModal', setState)}
          showModal={showCreateOrEditWebhookModal}
        />

        <CreateProviderPackageModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateProviderPackageModal', setState)}
          showModal={showCreateProviderPackageModal}
        />

        <CreateUserModal
          callbacks={callbacks('CreateUserModal', setState)}
          showModal={showCreateUserModal}
        />

        <EditProviderTradingDaysModal
          callbacks={callbacks('EditProviderTradingDaysModal', setState)}
          showModal={showEditProviderTradingDaysModal}
        />

        <GenerateReportModal
          // @ts-expect-error TS2322
          callbacks={callbacks('GenerateReportModal', setState)}
          showModal={showGenerateReportModal}
        />

        <ManageProviderApiSettingsModal
          callbacks={callbacks('ManageProviderApiSettingsModal', setState)}
          showModal={showManageProviderApiSettingsModal}
        />

        <CreateOrEditProviderDeepLinkModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditProviderDeepLinkModal', setState)}
          showModal={showCreateOrEditProviderDeepLinkModal}
        />

        <ManageProviderOptionsModal
          callbacks={callbacks('ManageProviderOptionsModal', setState)}
          showModal={showManageProviderOptionsModal}
        />

        <ManageProviderOrderDeliveryModal
          callbacks={callbacks('ManageProviderOrderDeliveryModal', setState)}
          showModal={showManageProviderOrderDeliveryModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
